import { useCallback, useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./TrendsPage.css";
import { getSalaryComparisonTwoRoles } from "./api";
import { parseNum, fmt, fmtPct, payChangeClass, parseReviewPeriod, FORECAST } from "./trendsFormat";
import TrendsMixedChart from "./TrendsMixedChart";
import TrendsSearchSelect from "./TrendsSearchSelect";
import SalaryBreakdownDialog from "./SalaryBreakdownDialog";

/**
 * TrendsTwoRoles - "Two Roles" tab.
 *
 * React port of double-role.component.ts: two roles side by side in ONE
 * territory, sharing that territory's inflation series. No KPI cards on
 * this tab (matching old). Comparing a role against itself is allowed
 * (no A !== B guard in the source). Chart = 2 salary bar series + 2
 * pay-change lines (solid A / dashed B) + dotted inflation line; two-tier
 * table header with per-role breakdown info icons; Excel export with the
 * merged two-row header mirroring the table.
 */
export default function TrendsTwoRoles({ startYear, endYear, startMonth, jobTitles, territories }) {
  const [territoryId, setTerritoryId] = useState(null);
  const [jobCodeA, setJobCodeA] = useState(null);
  const [jobCodeB, setJobCodeB] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  useEffect(() => {
    if (territoryId == null && territories.length > 0) setTerritoryId(territories[0].id);
  }, [territories, territoryId]);

  const canLoad = territoryId && jobCodeA && jobCodeB && startYear && endYear;
  const load = useCallback(() => {
    if (!canLoad) return;
    setLoading(true);
    getSalaryComparisonTwoRoles({ startYear, endYear, startMonth, territoryId, minervaJobCodeA: jobCodeA, minervaJobCodeB: jobCodeB })
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setLoading(false);
      });
  }, [canLoad, startYear, endYear, startMonth, territoryId, jobCodeA, jobCodeB]);

  useEffect(() => {
    load();
  }, [load]);

  const hasData = rows.length > 0;
  const hasForecast = rows.some((r) => r.is_forecasted_data === true);

  const territoryName = territories.find((t) => t.id === territoryId)?.name || "";
  const roleAName = jobTitles.find((j) => j.job_code === jobCodeA)?.name || jobCodeA || "Role A";
  const roleBName = jobTitles.find((j) => j.job_code === jobCodeB)?.name || jobCodeB || "Role B";

  function openBreakdown(row, which) {
    const code = which === "A" ? jobCodeA : jobCodeB;
    const name = which === "A" ? roleAName : roleBName;
    const { startMon, endMon, fullStartYear, fullEndYear } = parseReviewPeriod(row, startYear, endYear);
    setBreakdown({
      minervaJobCode: code,
      territoryId,
      startMonth: startMon,
      startYear: fullStartYear,
      endMonth: endMon,
      endYear: fullEndYear,
      label: `${name} — ${territoryName} (${row.review_period})`,
    });
  }

  async function exportExcel() {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Two Roles Analysis");
    // Merged two-row header mirroring the table's colspans:
    // A1:A2 Period | B1:B2 Inflation | C1:E1 Role A | F1:H1 Role B | I1:I2 Forecast
    ws.mergeCells("A1:A2"); ws.getCell("A1").value = "Review Period";
    ws.mergeCells("B1:B2"); ws.getCell("B1").value = "Total Inflation";
    ws.mergeCells("C1:E1"); ws.getCell("C1").value = roleAName;
    ws.mergeCells("F1:H1"); ws.getCell("F1").value = roleBName;
    ws.mergeCells("I1:I2"); ws.getCell("I1").value = "Partial";
    ["C2", "D2", "E2"].forEach((c, i) => { ws.getCell(c).value = ["Salary (£)", "Pay Change", "Real Change"][i]; });
    ["F2", "G2", "H2"].forEach((c, i) => { ws.getCell(c).value = ["Salary (£)", "Pay Change", "Real Change"][i]; });

    const NAVY = "FF1F4E78", CYAN = "FF66C3D0", BLUE = "FF2C6CB7";
    ["A1", "B1", "I1"].forEach((c) => {
      ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    });
    ["C1", "C2", "D2", "E2"].forEach((c) => {
      ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: CYAN } };
    });
    ["F1", "F2", "G2", "H2"].forEach((c) => {
      ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE } };
    });
    [1, 2].forEach((rn) => {
      ws.getRow(rn).eachCell((c) => {
        c.font = { bold: true, color: { argb: "FFFFFFFF" } };
        c.alignment = { horizontal: "center", vertical: "middle" };
      });
    });
    ws.columns = [
      { width: 18 }, { width: 13 },
      { width: 14 }, { width: 12 }, { width: 12 },
      { width: 14 }, { width: 12 }, { width: 12 },
      { width: 10 },
    ];

    const A_BAND = "FFF0FAFB", B_BAND = "FFF0F4FD", AMBER = "FFFFF3CD";
    rows.forEach((r) => {
      const row = ws.addRow([
        r.review_period,
        parseNum(r.inflation),
        parseNum(r.salary_a), parseNum(r.pay_change_a), parseNum(r.real_change_a),
        parseNum(r.salary_b), parseNum(r.pay_change_b), parseNum(r.real_change_b),
        r.is_forecasted_data ? "Yes" : "No",
      ]);
      row.getCell(3).numFmt = '£#,##0.00';
      row.getCell(6).numFmt = '£#,##0.00';
      [2, 4, 5, 7, 8].forEach((ci) => { row.getCell(ci).numFmt = '0.0"%"'; });
      [3, 4, 5].forEach((ci) => {
        row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: A_BAND } };
      });
      [6, 7, 8].forEach((ci) => {
        row.getCell(ci).fill = { type: "pattern", pattern: "solid", fgColor: { argb: B_BAND } };
      });
      [4, 5, 7, 8].forEach((ci) => {
        const v = row.getCell(ci).value;
        if (typeof v === "number" && v !== 0) {
          row.getCell(ci).font = { color: { argb: v > 0 ? "FF107C41" : "FFC0392B" }, bold: true };
        }
      });
      if (r.is_forecasted_data) {
        row.eachCell((c) => {
          c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: AMBER } };
        });
      }
    });
    ws.eachRow((row) => {
      row.eachCell((c) => {
        c.border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    });
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `Two-Roles_${territoryName}_${roleAName}_vs_${roleBName}.xlsx`.replace(/\s+/g, "-")
    );
  }

  const roleOptions = jobTitles.map((j) => ({ value: j.job_code, label: j.name, sub: j.sub_stack }));

  return (
    <div className="tr-tab">
      <div className="tr-mode-eyebrow">Two roles &times; one country vs inflation</div>
      <div className="tr-filters">
        <label className="tr-field">
          <span className="tr-field-label">Country<span className="tr-req">*</span></span>
          <select className="tr-select" value={territoryId ?? ""} onChange={(e) => setTerritoryId(Number(e.target.value))}>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
        <label className="tr-field tr-field-role">
          <span className="tr-field-label">Role A<span className="tr-req">*</span></span>
          <TrendsSearchSelect options={roleOptions} value={jobCodeA} placeholder="Select role A" onChange={setJobCodeA} />
        </label>
        <label className="tr-field tr-field-role">
          <span className="tr-field-label">Role B<span className="tr-req">*</span></span>
          <TrendsSearchSelect options={roleOptions} value={jobCodeB} placeholder="Select role B" onChange={setJobCodeB} />
        </label>
        {hasData && (
          <button type="button" className="tr-export-btn" onClick={exportExcel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
            Export to Excel
          </button>
        )}
      </div>

      {!hasData && !loading && (
        <div className="tr-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg>
          <p>Select a country and two roles above to view analysis.</p>
        </div>
      )}
      {loading && <p className="tr-status">Loading…</p>}

      {hasData && (
        <div hidden={loading}>
          {hasForecast && (
            <div className="tr-forecast-banner">
              Window includes partial data. Amber-shaded rows &amp; regions cover the available quarters.
              Sources: BoE / ECB / Fed / IMF / RBI / SARB / BCB.
            </div>
          )}

          <TrendsMixedChart
            seriesKey={JSON.stringify([territoryId, jobCodeA, jobCodeB])}
            labels={rows.map((r) => r.review_period)}
            forecastFlags={rows.map((r) => !!r.is_forecasted_data)}
            bars={[
              {
                label: `${roleAName} — salary`,
                fill: "rgba(102,195,208,0.70)",
                border: "#66c3d0",
                forecastFill: FORECAST.barFillStrong,
                values: rows.map((r) => parseNum(r.salary_a)),
              },
              {
                label: `${roleBName} — salary`,
                fill: "rgba(44,108,183,0.55)",
                border: "#2C6CB7",
                forecastFill: FORECAST.barFillSoft,
                values: rows.map((r) => parseNum(r.salary_b)),
              },
            ]}
            lines={[
              {
                label: `${roleAName} — pay change`, color: "#1a5f7a", dash: null,
                values: rows.map((r) => parseNum(r.pay_change_a)),
              },
              {
                label: `${roleBName} — pay change`, color: "#2C6CB7", dash: [5, 4],
                values: rows.map((r) => parseNum(r.pay_change_b)),
              },
              { label: "Inflation", color: "#E4572E", dash: [2, 2], values: rows.map((r) => parseNum(r.inflation)) },
            ]}
            height={340}
          />

          <div className="tr-table-wrap">
            <div className="tr-table-title">Period breakdown</div>
            <table className="tr-table tr-table-grouped">
              <thead>
                <tr>
                  <th rowSpan="2">REVIEW PERIOD</th>
                  <th rowSpan="2">TOTAL INFLATION</th>
                  <th colSpan="3" className="tr-group-a">{roleAName.toUpperCase()}</th>
                  <th colSpan="3" className="tr-group-b">{roleBName.toUpperCase()}</th>
                </tr>
                <tr>
                  <th className="tr-group-a">SALARY (£)</th>
                  <th className="tr-group-a">PAY CHANGE</th>
                  <th className="tr-group-a">REAL CHANGE</th>
                  <th className="tr-group-b">SALARY (£)</th>
                  <th className="tr-group-b">PAY CHANGE</th>
                  <th className="tr-group-b">REAL CHANGE</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.is_forecasted_data ? "tr-forecast-row" : ""}>
                    <td>
                      {r.review_period}
                      {r.is_forecasted_data && <span className="tr-f-badge" title="Partial">P</span>}
                    </td>
                    <td>{parseNum(r.inflation) === null ? "—" : `${parseNum(r.inflation)}%`}</td>
                    <td className="tr-band-a">
                      {fmt(r.salary_a)}
                      <button type="button" className="tr-info-btn" title="View salary breakdown" aria-label="View salary breakdown" onClick={() => openBreakdown(r, "A")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                      </button>
                    </td>
                    <td className={`tr-band-a ${payChangeClass(r.pay_change_a)}`}>{fmtPct(r.pay_change_a)}</td>
                    <td className={`tr-band-a ${payChangeClass(r.real_change_a)}`}>{fmtPct(r.real_change_a)}</td>
                    <td className="tr-band-b">
                      {fmt(r.salary_b)}
                      <button type="button" className="tr-info-btn" title="View salary breakdown" aria-label="View salary breakdown" onClick={() => openBreakdown(r, "B")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                      </button>
                    </td>
                    <td className={`tr-band-b ${payChangeClass(r.pay_change_b)}`}>{fmtPct(r.pay_change_b)}</td>
                    <td className={`tr-band-b ${payChangeClass(r.real_change_b)}`}>{fmtPct(r.real_change_b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SalaryBreakdownDialog open={!!breakdown} request={breakdown} onClose={() => setBreakdown(null)} />
    </div>
  );
}
